import {useRef} from 'react';
import {useTranslations} from 'next-intl';
import Container from '@/container';
import {Grid} from '@/grid';
import {SectionPadding} from '@/section';
import Link from '@/links';
import PictureWrapper from '@/ui/images-wrapper';
import ModalWrapper from '@/ui/modal-wrapper';
import StatsStrip from '@/ui/stats-strip';
import {H2, H3, P, PNoMargin} from '@/typography';
import {withBgNeutral200, withNeutral700} from '@/hocs/withColors';
import {withBoldWeight, withHeadingEyebrow, withSecondaryFontTestimonial} from '@/hocs/withFontType';
import {VideoModalButton} from '@/content/features/shared';
import sharedStyles from '../../industry-shared/customer-stories-section.module.scss';
import customersStyles from '../../customers/testimonials-section/styles.module.scss';
import styles from './customer-stories-section.module.scss';

const Neutral200Section = withBgNeutral200(SectionPadding);
const QuoteP = withSecondaryFontTestimonial(PNoMargin);
const NameP = withBoldWeight(PNoMargin);
const EyebrowNeutral700 = withNeutral700(withHeadingEyebrow(P as never));
const imgPath = '/static/images/industry-healthcare/';

type StoryStatLabel = {
  label: string;
};

// Stat values are kept inline (not translatable copy) and zipped with the labels from messages.
const STAT_VALUES = ['700+', '32', '~30'];

type Testimonial = {
  quote: string;
  nameTitle: string;
  company: string;
  imageUrl: string;
  imageAlt: string;
};

/** Grid forwards `children` via createProps; PropTypes-only export omits it from types. */
const FluidGrid = Grid as React.ComponentType<{
  fluid?: boolean;
  className?: string;
  tagName?: string;
  children?: React.ReactNode;
}>;

const TestimonialCard = ({testimonial}: {testimonial: Testimonial}) => (
  <article className={sharedStyles['testimonial-card']}>
    <div className={sharedStyles.media}>
      <PictureWrapper
        imgPath={imgPath}
        imageUrl={testimonial.imageUrl}
        imageTypeFallback="jpg"
        altImage={testimonial.imageAlt}
        className={sharedStyles['testimonial-image']}
        loading="lazy"
      />
    </div>

    <div className={sharedStyles['testimonial-content']}>
      <QuoteP className={sharedStyles.quote}>{testimonial.quote}</QuoteP>
      <NameP className={sharedStyles.name}>{testimonial.nameTitle}</NameP>
      <PNoMargin className={sharedStyles.company}>{testimonial.company}</PNoMargin>
    </div>
  </article>
);

const CustomerStoriesSection = () => {
  const t = useTranslations('industryHealthcare.customerStories');
  const videoButtonRef = useRef<HTMLButtonElement | null>(null);
  const statLabels = t.raw('featured.stats') as StoryStatLabel[];
  const stats = statLabels.map((stat, index) => ({
    value: STAT_VALUES[index] ?? '',
    label: stat.label
  }));
  const karo = t.raw('testimonial2') as Testimonial;
  const vatica = t.raw('testimonial1') as Testimonial;
  const hcl = t.raw('testimonial3') as Testimonial;

  return (
    <Neutral200Section>
      <Container>
        <FluidGrid fluid>
          <H2 className={sharedStyles.title}>{t('title')}</H2>

          <div className={styles['stories-grid']}>
            <article className={`${sharedStyles['featured-card']} ${styles.featured}`}>
              <div className={`${sharedStyles.media} ${sharedStyles['media--featured']}`}>
                <PictureWrapper
                  imgPath={imgPath}
                  responsiveImages={{
                    desktopImageUrl: 'qualderm',
                    mobileImageUrl: 'qualderm-mob'
                  }}
                  imageTypeFallback="jpg"
                  altImage={t('featured.imageAlt')}
                  className={styles['featured-image']}
                  loading="lazy"
                />
              </div>

              <div className={sharedStyles['featured-content']}>
                <StatsStrip items={stats} />
                <EyebrowNeutral700 className={sharedStyles.eyebrow}>
                  {t('featured.eyebrow')}
                </EyebrowNeutral700>
                <H3 className={sharedStyles['featured-title']}>{t('featured.title')}</H3>
                <P className={sharedStyles['featured-copy']}>{t('featured.description')}</P>
                <Link
                  href={t('featured.linkUrl')}
                  variant="cta-default-xs"
                  target={t('featured.linkTarget')}>
                  {t('featured.linkLabel')}
                </Link>
              </div>
            </article>

            <article className={`${sharedStyles['testimonial-card']} ${styles.video}`}>
              <ModalWrapper variant="modal-btn-wrapper" className={styles['video-modal']}>
                <div className={customersStyles['video-overlay']}>
                  <div className={customersStyles['video-overlay__logo']}>
                    <img
                      src={`${imgPath}${t('video.logoUrl')}.png`}
                      alt={t('video.logoAlt')}
                      width={244}
                      height={120}
                      loading="lazy"
                    />
                  </div>
                </div>
                <p className={`heading-3 ${customersStyles['video-caption']}`}>{t('video.caption')}</p>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl={t('video.imageUrl')}
                  imageTypeFallback="jpg"
                  altImage={t('video.imageAlt')}
                  className={styles['video-thumb']}
                  loading="lazy"
                  width={584}
                  height={336}
                  onClick={() => videoButtonRef.current?.click()}
                />
                <VideoModalButton
                  ref={videoButtonRef}
                  openName="healthcareCustomerVideo"
                  videoId={t('video.videoId')}
                  variant="image-green"
                  customAttributes={{
                    'data-gtm': 'healthcare-customer-stories-video'
                  }}
                  transcript={t('video.transcript')}
                />
              </ModalWrapper>
            </article>

            <div className={styles.karo}>
              <TestimonialCard testimonial={karo} />
            </div>

            <div className={styles.hcl}>
              <TestimonialCard testimonial={hcl} />
            </div>

            <div className={styles.vatica}>
              <TestimonialCard testimonial={vatica} />
            </div>
          </div>
        </FluidGrid>
      </Container>
    </Neutral200Section>
  );
};

export default CustomerStoriesSection;
